import React, { useState, useEffect } from "react";
import {
  Facebook,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Clock,
  ChevronRight,
  ChevronUp,
} from "lucide-react";
import { FaGoogle, FaWhatsapp } from "react-icons/fa";
import logo from "../assets/images/logo/Header-logo.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="bg-white text-black font-serif border-t-2 border-black">

      {/* ── FLOATING BUTTONS ── */}
      <div className="fixed bottom-6 right-4 z-50 flex flex-col items-center gap-3">
        <a
          href="https://wa.me/917042513698"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center text-white shadow-lg hover:scale-110 hover:bg-[#1ebe5d] transition-all duration-300"
        >
          <FaWhatsapp size={22} />
        </a>

        <a
          href="tel:+917042513698"
          aria-label="Call us"
          className="w-12 h-12 rounded-full bg-black flex items-center justify-center text-white shadow-lg hover:scale-110 hover:bg-gray-800 transition-all duration-300"
        >
          <Phone size={20} />
        </a>

        <button
          onClick={scrollToTop}
          aria-label="Back to top"
          className={`w-10 h-10 border-2 border-black bg-white flex items-center justify-center text-black shadow-md hover:bg-black hover:text-white transition-all duration-300 ${
            showTop
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4 pointer-events-none"
          }`}
        >
          <ChevronUp size={18} />
        </button>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-16 sm:pt-20 pb-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10 mb-16">

          {/* COL 1 — Logo & Mission */}
          <div className="sm:col-span-2 lg:col-span-1 space-y-7">
            <div>
              <img
                src={logo}
                alt="Rai & Associates"
                className="w-48 sm:w-56 h-auto object-contain mb-6"
              />
              <div className="w-10 h-[2px] bg-black" />
            </div>
            <p className="text-sm leading-relaxed text-gray-600 font-sans border-l-2 border-gray-400 pl-5 italic">
              Justice is the constant and perpetual will to allot to every man
              his due.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <SocialIcon
                icon={<Facebook size={16} />}
                href="https://www.facebook.com/people/Rai-Associates/61570798588995/"
              />
              <SocialIcon
                icon={<Instagram size={16} />}
                href="https://www.instagram.com/raiandassociates_/"
              />
              <SocialIcon
                icon={<FaGoogle size={16} />}
                href="https://www.google.com/search?q=Rai+%26+Associates&stick=H4sIAAAAAAAA_-NgU1I1qDC2NEhONTazMDRPMjZNSrK0MqgwSU5JTk2ytDBJMrE0NzExWcQqEJSYqaCm4FhcnJ-cmViSWgwATu7QmTwAAAA&hl=en-GB&mat=CVUK5D51i-5JElYBa0lj_5KaQFgjeVk54r7kISKSv6prOhQLrHZPalnJ_FPGa2MhqVLkLtWMCrIgwxx6ueUAUbZSxzWsWrp2I8exWWtZFEFN5_o3r18QLCuOn3L-mvmDgA&authuser=1"
              />
            </div>
          </div>

          {/* COL 2 — Quick Links */}
          <div>
            <FooterHeading label="Quick Links" />
            <ul className="space-y-4">
              <FooterLink label="Home" href="/" />
              <FooterLink label="About Us" href="/about-us" />
              <FooterLink label="Practice Areas" href="/services" />
              <FooterLink label="Latest Blogs" href="/blogs" />
              <FooterLink label="Contact Us" href="/contact-us" />
            </ul>
          </div>

          {/* COL 3 — Expertise */}
          <div>
            <FooterHeading label="Expertise" />
            <ul className="space-y-4">
              <FooterLink label="Anticipatory Bail" href="/services/anticipatory-bail" />
              <FooterLink label="Cheque Bounce (N.I. Act)" href="/services/cheque-bounce" />
              <FooterLink label="Murder & Homicide Defense" href="/services/murder" />
              <FooterLink label="Dowry Harassment (Sec 498A)" href="/services/dowry" />
              <FooterLink label="Debt Recovery Tribunal (DRT)" href="/services/drt" />
            </ul>
          </div>

          {/* COL 4 — Contact Info */}
          <div>
            <FooterHeading label="Contact Info" />
            <div className="space-y-6 font-sans">
              <div className="flex items-start gap-4 group">
                <MapPin size={17} className="shrink-0 mt-0.5 text-gray-500 group-hover:text-black transition-colors duration-300" />
                <span className="text-sm leading-snug text-gray-700 group-hover:text-black transition-colors duration-300">
                  2/3A, LGF, Jangpura A, New Delhi,  <br />
                  Delhi 110001                
                </span>
              </div>
              <div className="flex items-center gap-4 group">
                <Phone size={17} className="text-gray-500 group-hover:text-black transition-colors duration-300 shrink-0" />
                <a href="tel:+917042513698" className="text-sm font-bold text-black hover:text-gray-600 transition-colors duration-300 tracking-wide">
                  +91 70425 13698 <br /> +91 87682 62710
                </a>
              </div>
              <div className="flex items-center gap-4 group">
                <Mail size={17} className="text-gray-500 group-hover:text-black transition-colors duration-300 shrink-0" />
                <a href="mailto:raiandassociates369@gmail.com" className="text-sm text-gray-700 hover:text-black transition-colors duration-300 break-all">
                  raiandassociates369@gmail.com
                </a>
              </div>
              <div className="flex items-center gap-4 pt-5 border-t border-gray-200">
                <Clock size={15} className="text-gray-400 shrink-0" />
                <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-gray-500 font-sans">
                  Mon – Sat &nbsp;·&nbsp; 10:00 AM – 07:00 PM
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── COPYRIGHT BAR ── */}
        <div className="border-t border-gray-200 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[11px] uppercase tracking-[0.4em] text-gray-500 font-sans text-center sm:text-left">
            © {currentYear} Rai & Associates &nbsp;|&nbsp; All Rights Reserved
          </p>
          <p className="text-[11px] text-gray-500 tracking-widest uppercase font-sans text-center sm:text-right">
            Design & Developed by{" "}
            <a
              href="https://www.digimarqtechnology.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-black font-bold hover:text-gray-500 transition-colors duration-300 underline underline-offset-4"
            >
              Digimarq Technology Pvt Ltd
            </a>
          </p>
        </div>
      </div>

      {/* Bottom thick rule */}
      <div className="w-full h-[3px] bg-black" />
    </footer>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const FooterHeading = ({ label }) => (
  <div className="mb-8">
    <h3 className="text-xs font-black uppercase tracking-[0.45em] text-black mb-3">{label}</h3>
    <div className="w-6 h-[2px] bg-gray-300" />
  </div>
);

const FooterLink = ({ label, href }) => (
  <li className="flex items-center group cursor-pointer overflow-hidden list-none">
    <ChevronRight
      size={14}
      className="text-black -translate-x-5 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300 shrink-0"
    />
    <a
      href={href}
      className="text-sm text-gray-600 font-sans group-hover:text-black group-hover:translate-x-1 transition-all duration-300"
    >
      {label}
    </a>
  </li>
);

const SocialIcon = ({ icon, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="w-9 h-9 border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-black hover:text-white hover:border-black transition-all duration-300"
  >
    {icon}
  </a>
);

export default Footer;